import pluralize from 'pluralize';
import {Int} from '@store/@types/util';
import {Moment} from 'moment/moment';

const numberWords = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

interface Period {
  quantity: Int;
  unit: string;
}

interface Config {
  showUnitForSingles?: boolean;
  shouldPluralize?: boolean;
}

export function periodAsText(period: Period, config: Config = {}) {
  const {showUnitForSingles = false, shouldPluralize = true} = config;
  const {quantity, unit} = period;
  return (
    (quantity > 1 || showUnitForSingles ? (numberWords[quantity - 1] || quantity) + ' ' : '') +
    (shouldPluralize ? pluralize(unit, quantity) : unit)
  );
}

/**
 * Given a startDate and an endDate, both inclusive, return text that captures the period from startDate to endDate,
 * following these rules in this order:
 * 1st of month for startDate and end-of-month for endDate, then "x month(s)"
 * day Nth for startDate and day (N-1)th for endDate, then also "x month(s)"
 * Same day of week for startDate-1 and endDate, then "x week(s)"
 * All else "x day(s)"
 * @param startDate the start date.
 * @param endDate the end date.
 * @param config an object with optional keys showUnitForSingles (default true, e.g. "one month" vs "month")
 * and shouldPluralize (default false, e.g. "two days" vs "two day")
 * @returns {string}
 */
export function formatDuration(startDate: Moment, endDate: Moment, config: Config = {}) {
  if (!startDate && !endDate) return '';
  const startDateMinusOne = startDate.clone().subtract(1, 'day');
  const startDateOfMonthMinusOne = startDateMinusOne.date();
  const startDateOfMonth = startDate.date();
  const endDateOfMonth = endDate.date();
  if (
    (startDateOfMonth === 1 && endDateOfMonth === endDate.clone().endOf('month').date()) ||
    startDateOfMonthMinusOne === endDateOfMonth
  ) {
    return periodAsText(
      // The boolean of 'true' tells the diff to return a float rather than an int, then we round it.
      // Needed because a "month" is in general fuzzy math and moment can be wrong if there are not enough days
      // in between (or too many). In our cases we know we have "exact" number of months between.
      // See https://github.com/moment/moment/issues/3029
      {
        quantity: Math.round(endDate.diff(startDate, 'months', true)),
        unit: 'month',
      },
      config
    );
  }
  const startDayOfWeek = startDateMinusOne.day();
  const endDayOfWeek = endDate.day();
  if (startDayOfWeek === endDayOfWeek) {
    return periodAsText({quantity: endDate.diff(startDateMinusOne, 'weeks'), unit: 'week'}, config);
  }
  const diffDays = endDate.diff(startDateMinusOne, 'days');
  return periodAsText({quantity: diffDays, unit: 'day'}, config);
}
